import { FormikProvider, useField, useFormik } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';
import { Box } from 'reflexbox';
import { useDebouncedCallback } from 'use-debounce';

import { TargetIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { GoalChecklistItemType } from '@treams/treams-network';
import {
	CashIcon,
	CircleCheckmarkIcon,
	ContextMenu,
	Empty,
	Label,
	Paragraph,
	PlaceholderButton,
	Reorder,
	RulerIcon,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import CreateSubgoalCard from './CreateSubgoalCard';
import SubgoalCard from './SubgoalCard';

function CreateSubgoalTypeMenu(props: { onClick: (value: GoalChecklistItemType) => void }) {
	const { t } = useTranslation('', { keyPrefix: 'subgoals.add.type' });

	const options = [
		{
			title: t('checklist.title'),
			description: t('checklist.description'),
			icon: <CircleCheckmarkIcon />,
			value: GoalChecklistItemType.Checkbox,
		},
		{
			title: t('currency.title'),
			description: t('currency.description'),
			icon: <CashIcon />,
			value: GoalChecklistItemType.Currency,
		},
		{
			title: t('number.title'),
			description: t('number.description'),
			icon: <RulerIcon />,
			value: GoalChecklistItemType.Range,
		},
	];

	return (
		<ContextMenu width={420}>
			{options.map(({ title, icon, value, description }) => (
				<ContextMenu.Item
					key={value}
					title={<Label>{title}</Label>}
					icon={icon}
					description={
						<Paragraph small style={{ whiteSpace: 'nowrap' }}>
							{description}
						</Paragraph>
					}
					onClick={() => props.onClick(value)}
				/>
			))}
		</ContextMenu>
	);
}

function AddSubgoalButton(props: { onClick: (value: GoalChecklistItemType) => void }) {
	const { t } = useTranslation();

	return (
		<ContextMenu.Target menu={<CreateSubgoalTypeMenu onClick={props.onClick} />}>
			<Box>
				<PlaceholderButton type="secondary" title={t('subgoals.list.add.button')} />
			</Box>
		</ContextMenu.Target>
	);
}

const Subgoals = Skeleton(
	function Subgoals(props: { loading?: boolean; editable?: boolean; goalCompleted?: boolean }) {
		const { t } = useTranslation();
		const [{ value: subgoalIDs }, , { setValue: setSubgoalIDs }] = useField<string[]>('subgoalIDs');

		const [creating, setCreating] = useState<graphql.GoalChecklistItemType | undefined>(undefined);

		const addButton = creating ? (
			<CreateSubgoalCard type={creating} onDismiss={() => setCreating(undefined)} />
		) : (
			<AddSubgoalButton onClick={(newValue) => setCreating(newValue)} />
		);

		return (
			<Box width="100%">
				{subgoalIDs.length > 0 ? (
					<>
						<Reorder.Container
							onReorder={async (id, index) => {
								const update = subgoalIDs.filter((match) => match !== id);
								update.splice(index, 0, id);
								setSubgoalIDs(update);
							}}
						>
							{subgoalIDs.map((id, index) =>
								props.editable ? (
									<Reorder
										key={id}
										id={id}
										index={index}
										mb={`${tokens.gap.list}px`}
										loading={false}
										render={({ dragHandleProps, isDragging }) => (
											<SubgoalCard
												subgoalID={id}
												editable={props.editable}
												goalCompleted={props.goalCompleted}
												dragHandleProps={dragHandleProps}
												isDragging={isDragging}
											/>
										)}
									/>
								) : (
									<Box key={id} mb={`${tokens.gap.list}px`}>
										<SubgoalCard
											subgoalID={id}
											editable={props.editable}
											goalCompleted={props.goalCompleted}
										/>
									</Box>
								),
							)}
						</Reorder.Container>
						{props.editable && addButton}
					</>
				) : (
					<Box>
						{props.editable ? (
							addButton
						) : (
							<Skeleton.Empty
								contents={<Empty icon={<TargetIcon />} title={t('subgoals.list.empty.title')} />}
							>
								<Skeleton.Repeat count={2}>
									<Box mb={`${tokens.gap.list}px`}>
										<SubgoalCard.Skeleton />
									</Box>
								</Skeleton.Repeat>
							</Skeleton.Empty>
						)}
					</Box>
				)}
			</Box>
		);
	},
	function SubgoalsSkeleton() {
		return (
			<Skeleton.Repeat count={3}>
				<SubgoalCard.Skeleton mb={`${tokens.gap.list}px`} />
			</Skeleton.Repeat>
		);
	},
);

export function SubgoalsContext(props: { goal: graphql.Goal; editable?: boolean }) {
	const formik = useFormik({
		initialValues: {
			subgoals: _.chain(props.goal.checklistItems).keyBy('id').value(),
			subgoalIDs: props.goal.checklistItems.map(({ id }) => id),
		},
		onSubmit: () => {},
	});

	const [commit, { loading }] = graphql.useUpdateIndividualGoalMutation();

	const update = useDebouncedCallback(
		async () => {
			await commit({
				variables: {
					input: {
						id: props.goal.id,
						title: props.goal.title,
						description: props.goal.description,
						dueDate: props.goal.dueDate,
						linkedGoalDriverIDs: props.goal.linkedGoalDrivers?.map(({ id }) => id) ?? [],
						linkedGoalIDs: props.goal.linkedGoals?.map(({ id }) => id) ?? [],
						feasibility: props.goal.feasibility ?? undefined,
						labelID: props.goal.label?.id,
						competency: props.goal.competency
							? {
									competencyID: props.goal.competency!.competency.id,
									competencyLevelIndex: props.goal.competency!.competencyLevelIndex,
								}
							: undefined,
						checklistItems: formik.values.subgoalIDs.map((id) => ({
							..._.pick(formik.values.subgoals[id], [
								'id',
								'title',
								'description',
								'dueDate',
								'learningCourseID',
							]),
							progress: {
								type: formik.values.subgoals[id].progress.type,
								value: formik.values.subgoals[id].progress.value,
								lower: formik.values.subgoals[id].progress.lower ?? undefined,
								upper: formik.values.subgoals[id].progress.upper ?? undefined,
							},
						})),
						visibility: props.goal.visibility,
					},
				},
			});
		},
		1000,
		{ leading: true },
	);

	useUpdateEffect(() => {
		update();
	}, [JSON.stringify(formik.values)]);

	return (
		<FormikProvider value={formik}>
			<Subgoals
				loading={loading}
				editable={props.editable}
				goalCompleted={!!props.goal.completedAt}
			/>
		</FormikProvider>
	);
}

export default Subgoals;
